// §V2 EXACT PORT — the spike's app shell (docs/design-spikes/
// pomela-platform-v2/index.html lines 10-75 + 1260-1322): ambient/aurora
// living background · fixed desktop rail (brand → nav → rail-meter →
// secondary → user) · the ONLY mobile chrome: a floating glass menu-fab
// opening the drawer · global Ask-Pomy fab + slide-in panel (REAL
// /v1/copilot/chat) · #canvas as the single scroll container.
//
// Real data everywhere: credits + pool from /v1/credits, streak from the
// local ledger, identity from useAuth. Theme: the spike's data-theme
// attribute becomes .v2-dark/.v2-light computed from data-vs-theme +
// prefers-color-scheme (v2.css tokens key off those classes).
import { useCallback, useEffect, useRef, useState } from 'preact/hooks';
import type { ComponentChildren, JSX } from 'preact';
import { useAuth, ToastHost } from '@vibeshell/hooks';
import { Ic, tt } from './icons';
import { readStreak } from '../lib/streak';
import { goNav, activeNavId } from '../lib/nav-items';
import { usePomelaToastBridge, emitPomelaToast } from '../lib/toast-bridge';
import { performSignOut, onCrossTabSignOut } from './session-signout';

/** The spike boots with the rail COLLAPSED (studio.js adds .rail-collapsed at
 *  init); remember the user's toggle as a plain preference cache. */
const RAIL_PREF_KEY = 'vs:creator:v2-rail';
function initialCollapsed(): boolean {
  try {
    const pref = localStorage.getItem(RAIL_PREF_KEY);
    if (pref === 'open') return false;
    if (pref === 'collapsed') return true;
  } catch { /* preference cache only */ }
  return true;
}

function resolvedScheme(): 'dark' | 'light' {
  const pin = document.documentElement.getAttribute('data-vs-theme');
  if (pin === 'dark' || pin === 'light') return pin;
  return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

/** Rail/drawer destinations (Discover left with the community, D73). */
const NAV = [
  { id: 'home', hash: '', icon: 'home', key: 'v2.nav.home', en: 'Home' },
  { id: 'workspace', hash: 'apps', icon: 'grid', key: 'v2.nav.workspace', en: 'Workspace' },
  // §HUB — the Integration Hub is a first-class rail destination. It was added to the shared nav
  // registry first, but the rail keeps its own list, so registering it there alone left the catalog
  // reachable only by typing the hash. A catalog you must already know the URL of is not browsable.
  { id: 'connectors', hash: 'connectors', icon: 'plug', key: 'v2.nav.connectors', en: 'Integrations' },
  { id: 'guide', hash: 'guide', icon: 'book', key: 'v2.nav.learn', en: 'Learn' }
] as const;

export function V2Shell({ route, children }: { route: string; children: ComponentChildren }): JSX.Element {
  const { session, signOut, refresh } = useAuth();
  const [signingOut, setSigningOut] = useState(false);
  const [scheme, setScheme] = useState<'dark' | 'light'>(() => resolvedScheme());
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [drawer, setDrawer] = useState(false);
  const [pomy, setPomy] = useState(false);
  const canvasRef = useRef<HTMLElement>(null);
  // ONE toast host + ONE pomela:toast bridge for every routed surface —
  // per-screen hosts double-render every pill (and Support/Settings had none).
  usePomelaToastBridge();

  // theme: follow BOTH signals live (settings pin + OS)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const sync = (): void => setScheme(resolvedScheme());
    mq.addEventListener('change', sync);
    const mo = new MutationObserver(sync);
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-vs-theme'] });
    return () => { mq.removeEventListener('change', sync); mo.disconnect(); };
  }, []);

  // drawer + pomy close on navigation; canvas scrolls to top per screen
  useEffect(() => { setDrawer(false); canvasRef.current?.scrollTo({ top: 0 }); }, [route]);

  // global "Ask Pomy" — any screen can open the panel via this event
  useEffect(() => {
    const open = (): void => setPomy(true);
    window.addEventListener('pomela:open-pomy', open);
    return () => window.removeEventListener('pomela:open-pomy', open);
  }, []);

  // the editor owns its own Pomy surface — hide the global fab while mounted
  // (spike: .app.editor-mode). Broadcast by EditorWorkspace mount/unmount.
  const [editorMode, setEditorMode] = useState(false);
  useEffect(() => {
    const onMode = (e: Event): void => setEditorMode(!!(e as CustomEvent).detail);
    window.addEventListener('pomela:editor-mode', onMode);
    return () => window.removeEventListener('pomela:editor-mode', onMode);
  }, []);

  // Legacy surfaces still routed inside the shell (Upgrade/Copilot/
  // Admin) keep their TopBar hamburger, which dispatches pomela:open-sidebar.
  // The old Sidebar is gone — the event now opens the v2 drawer instead of
  // dying silently.
  useEffect(() => {
    const open = (): void => setDrawer(true);
    window.addEventListener('pomela:open-sidebar', open);
    return () => window.removeEventListener('pomela:open-sidebar', open);
  }, []);

  // Cross-tab sign-out — when the user signs out in ANOTHER Pomela tab, this
  // tab must reflect it. The server cookie is already gone, so refresh()
  // re-derives auth as signed-out (updating the shared session so every
  // useAuth() consumer re-renders), then we leave any authenticated-only
  // surface for the home canvas.
  useEffect(() => onCrossTabSignOut(() => { void refresh(); goNav(''); }), [refresh]);

  // Escape closes whichever overlay is open (drawer first, then Pomy) —
  // both are aria-hidden+inert when closed, so keyboard users can't get
  // trapped in invisible chrome.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      setDrawer((d) => (d ? false : d));
      setPomy((p) => (p ? false : p));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // credits + streak — the rail-meter (REAL numbers only; meter bar renders
  // only when the pool size is known).
  const [credits, setCredits] = useState<{ remaining: number; granted: number | null } | null>(null);
  useEffect(() => {
    let alive = true;
    fetch('/v1/credits', { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((b) => {
        if (alive && b?.credits && typeof b.credits.remaining === 'number') {
          setCredits({
            remaining: b.credits.remaining,
            granted: typeof b.credits.granted === 'number' && b.credits.granted > 0 ? b.credits.granted : null
          });
        }
      })
      .catch(() => {});
    return () => { alive = false; };
  }, [session.signedIn, session.accountId]);
  const streak = readStreak();

  const active = activeNavId(route) ?? (route === '' ? 'home' : route);
  const go = useCallback((hash: string) => { setDrawer(false); goNav(hash); }, []);

  // Sign out — the discoverable control (desktop rail + mobile drawer). Calls
  // the REAL endpoint via the hook (POST /v1/auth/sign-out, credentials
  // included) and broadcasts to sibling tabs. On success the shared session
  // flips to signed-out (chrome re-renders, CreatorRoot rebinds recents to
  // 'anon') and we land the user on the home canvas — a reload stays signed
  // out because the cookie is gone. On failure we surface an honest error and
  // leave the control live: no fake success, no dead button.
  const doSignOut = useCallback(async (): Promise<void> => {
    if (signingOut) return;
    setSigningOut(true);
    const ok = await performSignOut(signOut);
    setSigningOut(false);
    if (ok) {
      setDrawer(false);
      goNav('');
    } else {
      emitPomelaToast('error', tt('v2.signout.error', 'Couldn’t sign you out — check your connection and try again.'));
    }
  }, [signingOut, signOut]);

  const meter = (
    <button className="rail-meter" type="button" onClick={() => go('account')} aria-label={tt('nav.meterAria', 'Account — streak & credits')}>
      <div className="rm-row">
        <span className="rm-flame"><Ic n="flame" size={13} /></span>
        <span className="rm-streak">
          {streak.current > 0
            ? tt('nav.streak', `${streak.current} day streak`).replace('{n}', String(streak.current))
            : tt('nav.streakNone', 'Build today')}
        </span>
        {session.plan === 'pro' && <span className="rm-pro">PRO</span>}
      </div>
      {credits != null && (
        <div className="rm-sub"><b>{credits.remaining.toLocaleString()}</b> {tt('nav.creditsLeft', 'credits left')}</div>
      )}
      {credits != null && credits.granted != null && (
        <div className="rm-bar"><i style={`--v:${Math.max(2, Math.min(100, Math.round((credits.remaining / credits.granted) * 100)))}%`} /></div>
      )}
    </button>
  );

  const navLinks = (drawerMode: boolean): JSX.Element => (
    <>
      {NAV.map((n) => (
        <a
          key={n.id}
          className={`navi${active === n.id ? ' on' : ''}`}
          href={`#${n.hash}`}
          onClick={(e) => { e.preventDefault(); go(n.hash); }}
          aria-current={active === n.id ? 'page' : undefined}
          aria-label={tt(n.key, n.en)}
        >
          <span className="ico"><Ic n={n.icon} /></span>
          <span className="lbl">{tt(n.key, n.en)}</span>
        </a>
      ))}
      {/* Search/⌘K is desktop-rail chrome only — the spike's drawer has no
          search row (and a kbd hint on a touch surface reads as noise). */}
      {!drawerMode && (
        <button
          className="navi"
          type="button"
          aria-label={tt('nav.search', 'Search')}
          onClick={() => { setDrawer(false); window.dispatchEvent(new CustomEvent('pomela:open-palette')); }}
        >
          <span className="ico"><Ic n="search" /></span>
          <span className="lbl">{tt('nav.search', 'Search')}</span>
          <kbd className="navi-kbd">⌘K</kbd>
        </button>
      )}
    </>
  );

  const secondary = (sm: boolean): JSX.Element => (
    <>
      <a className={`navi${sm ? ' sm' : ''}${route === 'help' ? ' on' : ''}`} href="#help" aria-label={tt('v2.nav.support', 'Support')} onClick={(e) => { e.preventDefault(); go('help'); }}>
        <span className="ico"><Ic n="life" /></span><span className="lbl">{tt('v2.nav.support', 'Support')}</span>
      </a>
      <a className={`navi${sm ? ' sm' : ''}${route === 'settings' ? ' on' : ''}`} href="#settings" aria-label={tt('nav.settings', 'Settings')} onClick={(e) => { e.preventDefault(); go('settings'); }}>
        <span className="ico"><Ic n="gear" /></span><span className="lbl">{tt('nav.settings', 'Settings')}</span>
      </a>
      {session.isAdmin && (
        <a className={`navi${sm ? ' sm' : ''}${route === 'admin' ? ' on' : ''}`} href="#admin" aria-label={tt('nav.admin', 'Founder')} onClick={(e) => { e.preventDefault(); go('admin'); }}>
          <span className="ico"><Ic n="shield" /></span><span className="lbl">{tt('nav.admin', 'Founder')}</span>
        </a>
      )}
    </>
  );

  const initial = (session.email?.[0] || 'P').toUpperCase();
  const userName = session.signedIn ? (session.email || tt('nav.account', 'Account')) : tt('nav.signIn', 'Sign in');
  const userSub = session.isAdmin ? tt('nav.ownerGodMode', 'Owner · God Mode') : session.plan === 'pro' ? 'Pro' : 'Free';

  return (
    <div
      className={`app v2-${scheme}${collapsed ? ' rail-collapsed' : ''}${drawer ? ' drawer-open' : ''}${pomy ? ' pomy-open' : ''}${editorMode ? ' editor-mode' : ''}`}
      data-v2
    >
      {/* living background */}
      <div className="ambient" aria-hidden="true"><div className="aurora" /></div>

      {/* desktop rail */}
      <aside className="rail" id="rail">
        <div className="brand-row">
          <a className="brand" href="#" onClick={(e) => { e.preventDefault(); go(''); }} aria-label={tt('nav.ariaHome', 'Pomela home')}>
            <span className="mark" aria-hidden="true">
              <svg viewBox="0 0 32 32" width="18" height="18"><path d="M9 6h9a8 8 0 0 1 0 16h-4v4H9V6Zm5 6v4h4a2 2 0 0 0 0-4h-4Z" fill="#fff" /></svg>
            </span>
            <span className="brand-word">Pomela <em>studio</em></span>
          </a>
          <button
            className="rail-collapse-btn"
            type="button"
            aria-label={tt('v2.nav.collapse', 'Collapse sidebar')}
            aria-expanded={!collapsed}
            onClick={() => setCollapsed((c) => {
              const next = !c;
              try { localStorage.setItem(RAIL_PREF_KEY, next ? 'collapsed' : 'open'); } catch { /* pref cache */ }
              return next;
            })}
          >
            <Ic n="chevl" size={15} />
          </button>
        </div>

        <nav className="nav-primary" aria-label="Primary">{navLinks(false)}</nav>
        {meter}
        <nav className="nav-secondary" aria-label="Secondary">{secondary(true)}</nav>

        <a className="rail-user" href="#account" onClick={(e) => { e.preventDefault(); go('account'); }}>
          <span className="ava">{initial}</span>
          <span className="ru-txt"><b>{userName}</b><span>{userSub}</span></span>
          <span className="ru-go"><Ic n="chev" size={14} /></span>
        </a>
        {/* Sign out — anchored to the profile area so it's the first thing a
            user looking to switch accounts finds. Icon-only + aria-label keeps
            it usable when the rail is collapsed; hidden entirely when there's
            no session to end. */}
        {session.signedIn && (
          <button
            className="navi sm rail-signout"
            type="button"
            onClick={() => void doSignOut()}
            disabled={signingOut}
            aria-label={tt('acc.signOut', 'Sign out')}
            title={tt('acc.signOut', 'Sign out')}
          >
            <span className="ico"><Ic n="logout" /></span>
            <span className="lbl">{signingOut ? tt('state.loading', 'Loading…') : tt('acc.signOut', 'Sign out')}</span>
          </button>
        )}
      </aside>

      {/* the only mobile chrome */}
      <button className="menu-fab only-mobile" type="button" aria-label={tt('app.openMenu', 'Open menu')} onClick={() => setDrawer(true)}>
        <Ic n="menu" size={18} />
      </button>

      <div className="main">
        <main className="canvas" id="canvas" ref={canvasRef}>
          {children}
        </main>
      </div>

      {/* mobile drawer — open/close state lives on the app root
          (.drawer-open / .pomy-open, exactly like the spike's JS) */}
      <div className="drawer-scrim" onClick={() => setDrawer(false)} />
      <aside className="drawer" aria-label="Menu" aria-hidden={!drawer} inert={drawer ? undefined : true}>
        <div className="drawer-head">
          <a className="brand" href="#" onClick={(e) => { e.preventDefault(); go(''); }} aria-label={tt('nav.ariaHome', 'Pomela home')}>
            <span className="mark" aria-hidden="true">
              <svg viewBox="0 0 32 32" width="18" height="18"><path d="M9 6h9a8 8 0 0 1 0 16h-4v4H9V6Zm5 6v4h4a2 2 0 0 0 0-4h-4Z" fill="#fff" /></svg>
            </span>
            <span className="brand-word">Pomela</span>
          </a>
          <button className="icon-btn" type="button" aria-label={tt('common.close', 'Close')} onClick={() => setDrawer(false)}><Ic n="x" /></button>
        </div>
        <nav className="drawer-nav">
          {navLinks(true)}
          <a className="navi" href="#copilot" onClick={(e) => { e.preventDefault(); setDrawer(false); setPomy(true); }}>
            <span className="ico"><Ic n="spark" /></span><span className="lbl">{tt('v2.nav.askPomy', 'Ask Pomy')}</span>
          </a>
          <div className="drawer-div" />
          {secondary(true)}
          <a className="navi sm" href="#account" onClick={(e) => { e.preventDefault(); go('account'); }}>
            <span className="ico"><Ic n="user" /></span><span className="lbl">{tt('nav.account', 'Account')}</span>
          </a>
          {/* Sign out — the mobile counterpart of the rail control, right under
              Account so it's reachable in one tap from the menu (no scrolling
              to the Account screen's danger zone). */}
          {session.signedIn && (
            <button
              className="navi sm rail-signout"
              type="button"
              onClick={() => void doSignOut()}
              disabled={signingOut}
              aria-label={tt('acc.signOut', 'Sign out')}
            >
              <span className="ico"><Ic n="logout" /></span>
              <span className="lbl">{signingOut ? tt('state.loading', 'Loading…') : tt('acc.signOut', 'Sign out')}</span>
            </button>
          )}
        </nav>
        {meter}
      </aside>

      {/* global Ask Pomy */}
      <button className="pomy-fab" type="button" aria-label={tt('v2.pomy.aria', 'Ask Pomy — ideas, advice, anything about Pomela')} onClick={() => setPomy(true)}>
        <Ic n="spark" size={20} />
      </button>
      <div className="pomy-scrim" onClick={() => setPomy(false)} />
      <PomyPanel open={pomy} onClose={() => setPomy(false)} />
      <ToastHost />
    </div>
  );
}

/** Global Pomy — the spike's slide-in panel wired to the REAL copilot. */
function PomyPanel({ open, onClose }: { open: boolean; onClose: () => void }): JSX.Element {
  const [msgs, setMsgs] = useState<Array<{ role: 'user' | 'assistant'; content: string }>>([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  const send = useCallback(async (text: string) => {
    const q = text.trim();
    if (!q || busy) return;
    setInput('');
    setMsgs((m) => [...m, { role: 'user', content: q }]);
    setBusy(true);
    try {
      const res = await fetch('/v1/copilot/chat', {
        method: 'POST',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ messages: [...msgs, { role: 'user', content: q }].slice(-12), mode: 'general' })
      });
      const j = res.ok ? ((await res.json()) as { reply?: string; message?: string }) : null;
      const reply = j?.reply || j?.message;
      setMsgs((m) => [...m, {
        role: 'assistant',
        content: reply || tt('v2.pomy.err', 'I couldn’t reach Pomy just now — try again in a moment.')
      }]);
    } catch {
      setMsgs((m) => [...m, { role: 'assistant', content: tt('v2.pomy.err', 'I couldn’t reach Pomy just now — try again in a moment.') }]);
    } finally {
      setBusy(false);
      window.setTimeout(() => scrollRef.current?.scrollTo({ top: 9e6 }), 30);
    }
  }, [busy, msgs]);

  const chips = [
    tt('v2.pomy.chip0', 'Give me an app idea for my business'),
    tt('v2.pomy.chip1', 'What’s the difference between Quick and Deep?'),
    tt('v2.pomy.chip2', 'How do credits work?'),
    tt('v2.pomy.chip3', 'What can I actually build here?')
  ];

  return (
    <aside className="pomy-panel" aria-label="Ask Pomy" aria-hidden={!open} inert={open ? undefined : true}>
      <div className="pomy-head">
        <span className="pomy-av"><Ic n="spark" /></span>
        <div className="pomy-head-txt"><b>Pomy</b><span>{tt('v2.pomy.sub', 'Ideas, advice, anything about Pomela')}</span></div>
        <button className="icon-btn sm mla" type="button" aria-label={tt('common.close', 'Close')} style={{ width: 30, height: 30 }} onClick={onClose}><Ic n="x" size={14} /></button>
      </div>
      <div className="pomy-body" ref={scrollRef}>
        <div className="chat">
          <div className="msg bot"><span className="m-av"><Ic n="spark" size={13} /></span><div className="bub">{tt('v2.pomy.hello', 'Hey, I’m Pomy. Ask me for app ideas, how something in Pomela works, or advice on what to build next.')}</div></div>
          {msgs.map((m, i) => (
            <div key={i} className={`msg${m.role === 'user' ? ' me' : ' bot'}`}>
              {m.role === 'assistant' && <span className="m-av"><Ic n="spark" size={13} /></span>}
              <div className="bub">{m.content}</div>
            </div>
          ))}
          {busy && <div className="msg bot"><span className="m-av"><Ic n="spark" size={13} /></span><div className="bub bub-typing"><i /><i /><i /></div></div>}
        </div>
        {msgs.length === 0 && (
          <>
            <span className="sec-label" style={{ display: 'block', margin: '18px 0 10px' }}>{tt('v2.try', 'Try')}</span>
            <div className="pomy-chips">
              {chips.map((c) => <button key={c} className="pchip" type="button" onClick={() => void send(c)}>{c}</button>)}
            </div>
          </>
        )}
      </div>
      <div className="chat-input">
        <textarea
          rows={1}
          value={input}
          placeholder={tt('v2.pomy.ph', 'Ask Pomy anything…')}
          onInput={(e) => setInput((e.target as HTMLTextAreaElement).value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(input); } }}
        />
        <button className="chat-send" type="button" aria-label={tt('v2.send', 'Send')} onClick={() => void send(input)}><Ic n="send" size={15} /></button>
      </div>
    </aside>
  );
}
